/**
 * A small, safe Markdown reader for the lesson explanations (headings, paragraphs, lists, bold and
 * italics). It builds React elements (never raw HTML), so a lesson can never inject markup.
 */

import { Fragment, type ReactNode } from "react";

/** « **bold** » and « *italics* » inside a line. */
export function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g;
  let last = 0;
  let index = 0;
  for (const match of text.matchAll(pattern)) {
    const start = match.index ?? 0;
    if (start > last) out.push(text.slice(last, start));
    const token = match[0];
    if (token.startsWith("**")) out.push(<strong key={index}>{token.slice(2, -2)}</strong>);
    else out.push(<em key={index}>{token.slice(1, -1)}</em>);
    index += 1;
    last = start + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

type Block =
  | { kind: "h"; level: number; text: string }
  | { kind: "p"; text: string }
  | { kind: "ul" | "ol"; items: string[] }
  | { kind: "table"; head: string[]; rows: string[][] };

/** « | a | b | » → ["a", "b"]. */
function cells(line: string): string[] {
  return line.replace(/^\|/, "").replace(/\|$/, "").split("|").map((cell) => cell.trim());
}

export function blocks(source: string): Block[] {
  const out: Block[] = [];
  let paragraph: string[] = [];
  const flush = () => {
    if (paragraph.length) out.push({ kind: "p", text: paragraph.join(" ") });
    paragraph = [];
  };
  for (const raw of source.split("\n")) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    if (line.startsWith("|") && line.endsWith("|") && line.length > 1) {
      // a Markdown table: the first row is the header, the « |---| » row is skipped
      flush();
      const row = cells(line);
      if (row.every((cell) => /^:?-{2,}:?$/.test(cell))) continue;
      const previous = out[out.length - 1];
      if (previous && previous.kind === "table") previous.rows.push(row);
      else out.push({ kind: "table", head: row, rows: [] });
      continue;
    }
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    const bullet = /^[-*•]\s+(.*)$/.exec(line);
    const numbered = /^\d+[.)]\s+(.*)$/.exec(line);
    if (heading) {
      flush();
      out.push({ kind: "h", level: heading[1]?.length ?? 2, text: heading[2] ?? "" });
    } else if (bullet || numbered) {
      flush();
      const kind = bullet ? "ul" : "ol";
      const text = (bullet ? bullet[1] : numbered?.[1]) ?? "";
      const previous = out[out.length - 1];
      if (previous && previous.kind === kind) previous.items.push(text);
      else out.push({ kind, items: [text] });
    } else {
      paragraph.push(line);
    }
  }
  flush();
  return out;
}

export function Markdown({ source, lang }: { source: string; lang?: string }) {
  return (
    <div className="ac-md" lang={lang}>
      {blocks(source).map((block, index) => {
        if (block.kind === "h") {
          // Lesson headings sit under the h2 of the card: always h3 / h4 on screen.
          const Tag = block.level <= 2 ? "h3" : "h4";
          return <Tag key={index}>{inline(block.text)}</Tag>;
        }
        if (block.kind === "p") return <p key={index}>{inline(block.text)}</p>;
        if (block.kind === "table")
          return (
            <div key={index} className="ac-md__table">
              <table>
                <thead>
                  <tr>
                    {block.head.map((cell, i) => (
                      <th key={i} scope="col">
                        {inline(cell)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, r) => (
                    <tr key={r}>
                      {row.map((cell, i) => (
                        <td key={i}>{inline(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        const List = block.kind;
        return (
          <List key={index}>
            {block.items.map((item, i) => (
              <li key={i}>
                <Fragment>{inline(item)}</Fragment>
              </li>
            ))}
          </List>
        );
      })}
    </div>
  );
}
